"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Users, Car, Plane, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ROUTE_PRICES, formatPrice, type Currency } from "@/lib/pricing";
import { useCurrency } from "@/hooks/useCurrency";
import { CurrencySelector, CurrencyNote } from "@/components/CurrencySelector";
function getCurrencyDescription(currency: Currency): string {
  switch (currency) {
    case "EGP":
      return "Währung: Ägyptisches Pfund (EGP) · Inklusive Maut & Steuern";
    case "EUR":
      return "Währung: Euro (EUR), ungefähre Umrechnung · Inklusive Maut & Steuern";
    case "USD":
      return "Währung: US-Dollar (USD), ungefähre Umrechnung · Inklusive Maut & Steuern";
    default:
      return "Währung: Inklusive Maut & Steuern";
  }
}
export default function DestinationsTable() {
  const {
    currency
  } = useCurrency();
  return <section id="all-routes-fixed-prices" data-nav="light" className="relative bg-background text-foreground py-20 md:py-24 border-b border-border selection:bg-primary/20 selection:text-foreground">
      <div className="max-w-[1136px] mx-auto px-6">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="max-w-3xl">
          {/* Eyebrow with Signature Dot */}
          <div className="flex items-center gap-2 mb-3">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              Feste Preise pro Fahrzeug
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight text-foreground leading-[1.2]">
            Alle Routen &amp; Festpreise
          </h1>

          {/* Signature Hairline Rule */}
          <div className="relative my-6 h-px w-full max-w-md bg-border" aria-hidden="true">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-primary" />
          </div>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Jeder Preis gilt pro Fahrzeug, ist im Voraus festgelegt und beinhaltet alle Autobahngebühren sowie Parkgebühren am Flughafen Hurghada. Wir verfolgen die Ankunftszeit und mögliche Verspätungen deines Fluges kostenlos. Dein Transfer beinhaltet bis zu 60 Minuten kostenlose Wartezeit ab dem vereinbarten Treffzeitpunkt – ohne Überraschungen bei der Ankunft.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium text-foreground/85">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />
              <span>Direkter Tür-zu-Tür-Transfer</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Plane className="size-4 text-primary" aria-hidden="true" />
              <span>Kostenlose Flugüberwachung</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="size-4 text-primary" aria-hidden="true" />
              <span>60 Min. kostenlose Wartezeit</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-primary" aria-hidden="true" />
              <span>Private Abholung von Tür zu Tür</span>
            </div>
          </div>
        </motion.div>

        {/* Currency Controls & Note */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.05,
        ease: "easeOut"
      }} className="mt-8 flex flex-col md:flex-row md:items-start justify-between gap-6 p-6 rounded-md border border-border bg-card/60">
          <CurrencySelector locale="de" />
          <CurrencyNote locale="de" className="max-w-xl" />
        </motion.div>

        {/* Ledger / Table Container */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        delay: 0.1,
        ease: "easeOut"
      }} className="mt-12 overflow-hidden rounded-md border border-border bg-card shadow-xs">
          {/* Table Header Description Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border bg-secondary/50 px-5 py-3.5 text-xs text-muted-foreground gap-2">
            <span>Abfahrt: Internationaler Flughafen Hurghada (HRG)</span>
            <span>{getCurrencyDescription(currency)}</span>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="w-[45%] py-4 pl-6 text-xs font-semibold uppercase tracking-wider text-foreground">
                    Ziel
                  </TableHead>
                  <TableHead className="w-[27.5%] py-4 px-4 text-xs font-semibold uppercase tracking-wider text-foreground">
                    <div className="flex items-center gap-2">
                      <Car className="size-4 text-primary" aria-hidden="true" />
                      <span>Standard-Pkw – bis zu 4 Personen</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[27.5%] py-4 pr-6 text-xs font-semibold uppercase tracking-wider text-foreground">
                    <div className="flex items-center gap-2">
                      <Users className="size-4 text-primary" aria-hidden="true" />
                      <span>Minivan – bis zu 10 Personen</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ROUTE_PRICES.map((route, i) => <TableRow key={route.id} id={route.slug} data-index={i} className="border-b border-border/70 hover:bg-accent/40 transition-[background-color] duration-150">
                    <TableCell className="py-4 pl-6 font-medium text-foreground">
                      <div className="flex items-center gap-2.5">
                        <span className="text-sm font-semibold text-foreground">
                          {route.name}
                        </span>
                        {route.popular && <span className="inline-flex items-center rounded-xs bg-[var(--chart-2)]/15 px-2 py-0.5 text-[11px] font-medium text-[var(--chart-2)] border border-[var(--chart-2)]/30">
                            Beliebte Route
                          </span>}
                      </div>
                      {route.slug === "route-el-gouna" && <div className="mt-1">
                          <Link href="/transfer-hurghada-el-gouna/" className="text-xs text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
                            Ratgeber: Transfer Hurghada – El Gouna
                          </Link>
                        </div>}
                    </TableCell>
                    <TableCell className="py-4 px-4">
                      <div className="inline-flex items-baseline gap-1">
                        <span className="text-base font-semibold text-foreground">
                          {formatPrice(route.carEgp, currency, "de")}
                        </span>
                        <span className="text-xs text-muted-foreground">/ Fahrzeug</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 pr-6">
                      <div className="inline-flex items-baseline gap-1">
                        <span className="text-base font-semibold text-foreground">
                          {formatPrice(route.vanEgp, currency, "de")}
                        </span>
                        <span className="text-xs text-muted-foreground">/ Fahrzeug</span>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </motion.div>

        {/* Bus Note Below Table */}
        <div className="mt-3 text-xs text-muted-foreground">
          Bus: für Gruppen ab 10 Personen – Preis auf Anfrage.
        </div>

        {/* Bottom Booking Prompt & CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 p-6 rounded-md bg-secondary/60 border border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Bereit für Ihre Fahrt?
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Senden Sie uns Ihre Reisedaten – unser Team bestätigt Fahrzeugklasse, Festpreis und alle Details nach Eingang Ihrer Anfrage persönlich.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-md shrink-0">
            <Link href="/book-transfer-de/">
              <span>Privaten Transfer anfragen</span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}